const TICKER_CONFIGS = [
  {
    trackSelector: ".ticker__slider",
    slideSelector: ".ticker__slide",
    imageSelector: ".ticker__slider-img",
    mobileOnly: false,
  },
  {
    trackSelector: ".countries--grid",
    slideSelector: ".country--item",
    mobileOnly: true,
  },
];

const BASE_SPEED = 1.2;
const MAX_SPEED = 6;
const MOBILE_BREAKPOINT = 991;
const MOBILE_SPEED_MULTIPLIER = 0.65;
const MOBILE_MAX_SPEED = 2.2;
const DRAG_START_THRESHOLD = 8;
const VERTICAL_GESTURE_BIAS = 1.15;

const RUNTIME_CLASSES = {
  wrapper: "ticker-runtime-wrapper",
  track: "ticker-runtime-track",
  mover: "ticker-runtime-mover",
  slide: "ticker-runtime-slide",
  image: "ticker-runtime-image",
  dragging: "is-dragging",
};

function isMobileViewport() {
  if (typeof window.matchMedia === "function") {
    return window.matchMedia(`(max-width:${MOBILE_BREAKPOINT}px)`).matches;
  }
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

export function initTicker(config) {
  if (config.mobileOnly && !isMobileViewport()) return null;

  const track = document.querySelector(config.trackSelector);
  if (!track || track.dataset.tickerInitialized === "true") return null;

  const wrapper = track.parentElement;
  const originalSlides = Array.from(track.querySelectorAll(config.slideSelector));
  if (!wrapper || !originalSlides.length) return null;

  track.dataset.tickerInitialized = "true";
  wrapper.classList.add(RUNTIME_CLASSES.wrapper);
  track.classList.add(RUNTIME_CLASSES.track);

  const mover = document.createElement("div");
  mover.classList.add(RUNTIME_CLASSES.mover);

  originalSlides.forEach((slide) => {
    slide.classList.add(RUNTIME_CLASSES.slide);
    mover.appendChild(slide);
  });

  if (config.imageSelector) {
    mover.querySelectorAll(config.imageSelector).forEach((image) => {
      image.classList.add(RUNTIME_CLASSES.image);
    });
  }

  track.appendChild(mover);

  const resolvedBaseSpeed = config.baseSpeed ?? BASE_SPEED;
  const getBaseSpeed = () => resolvedBaseSpeed * (isMobileViewport() ? MOBILE_SPEED_MULTIPLIER : 1);
  const getMaxSpeed = () =>
    isMobileViewport() ? Math.min(MAX_SPEED, MOBILE_MAX_SPEED) : MAX_SPEED;

  let slideWidth = 0;
  let originalSetWidth = 0;
  let position = 0;
  let reversePositionInitialized = false;
  let velocity = getBaseSpeed();
  let targetVelocity = getBaseSpeed();
  let animationFrameId = null;
  let isVisible = false;
  let isDragging = false;
  let shouldIgnorePointer = false;
  let activePointerId = null;
  let startPointerX = 0;
  let startPointerY = 0;
  let lastPointerX = 0;
  let momentumSamples = [];

  const ensureClones = () => {
    mover.querySelectorAll('[aria-hidden="true"]').forEach((clone) => clone.remove());
    if (slideWidth <= 0) return;

    const setsNeeded = Math.max(
      8,
      Math.ceil((window.innerWidth * 2) / (originalSlides.length * slideWidth)) + 1
    );

    for (let setIndex = 0; setIndex < setsNeeded; setIndex += 1) {
      originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        mover.appendChild(clone);
      });
    }
  };

  const updateMetrics = () => {
    const firstSlide = mover.querySelector(config.slideSelector);
    if (!firstSlide) return;

    const nextSlideWidth = firstSlide.getBoundingClientRect().width;
    if (nextSlideWidth === slideWidth && nextSlideWidth > 0) return;

    slideWidth = nextSlideWidth;
    ensureClones();
    originalSetWidth = originalSlides.length * slideWidth;

    if (originalSetWidth <= 0) return;

    if (config.reverse) {
      if (!reversePositionInitialized) {
        position = originalSetWidth;
        reversePositionInitialized = true;
      } else {
        position = ((position % originalSetWidth) + originalSetWidth) % originalSetWidth;
        if (position === 0) position = originalSetWidth;
      }
    } else {
      position = ((position % originalSetWidth) + originalSetWidth) % originalSetWidth;
    }
  };

  const startAnimation = () => {
    if (animationFrameId !== null) return;
    animationFrameId = window.requestAnimationFrame(tick);
  };

  function tick() {
    if (!isVisible) {
      animationFrameId = null;
      return;
    }

    const baseSpeed = getBaseSpeed();

    if (!isDragging) {
      velocity += (targetVelocity - velocity) * (isMobileViewport() ? 0.12 : 0.06);
      targetVelocity += (baseSpeed - targetVelocity) * (isMobileViewport() ? 0.12 : 0.02);
      position += config.reverse ? -velocity : velocity;
    }

    if (originalSetWidth > 0) {
      if (config.reverse && position <= 0) position += originalSetWidth;
      if (!config.reverse && position >= originalSetWidth) position -= originalSetWidth;
    }

    mover.style.transform = `translate3d(${(-position).toFixed(3)}px,0,0)`;
    animationFrameId = window.requestAnimationFrame(tick);
  }

  updateMetrics();

  if (typeof window.ResizeObserver === "function") {
    const resizeObserver = new window.ResizeObserver(updateMetrics);
    resizeObserver.observe(originalSlides[0]);
  } else {
    window.addEventListener("resize", updateMetrics);
  }

  if (typeof window.IntersectionObserver === "function") {
    const intersectionObserver = new window.IntersectionObserver(
      (entries) => {
        isVisible = Boolean(entries[0]?.isIntersecting);
        if (isVisible) startAnimation();
      },
      { threshold: 0 }
    );
    intersectionObserver.observe(wrapper);
  } else {
    isVisible = true;
    startAnimation();
  }

  wrapper.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;

    activePointerId = event.pointerId;
    shouldIgnorePointer = false;
    isDragging = false;
    startPointerX = event.clientX;
    startPointerY = event.clientY;
    lastPointerX = event.clientX;
    momentumSamples = [];
  });

  wrapper.addEventListener("pointermove", (event) => {
    if (activePointerId !== event.pointerId || shouldIgnorePointer) return;

    if (!isDragging) {
      const totalDeltaX = startPointerX - event.clientX;
      const totalDeltaY = startPointerY - event.clientY;
      const absoluteDeltaX = Math.abs(totalDeltaX);
      const absoluteDeltaY = Math.abs(totalDeltaY);

      if (absoluteDeltaX < DRAG_START_THRESHOLD && absoluteDeltaY < DRAG_START_THRESHOLD) {
        return;
      }

      if (isMobileViewport() && absoluteDeltaY > absoluteDeltaX * VERTICAL_GESTURE_BIAS) {
        shouldIgnorePointer = true;
        return;
      }

      isDragging = true;
      wrapper.setPointerCapture?.(event.pointerId);
      wrapper.classList.add(RUNTIME_CLASSES.dragging);
    }

    const pointerDelta = lastPointerX - event.clientX;
    position += config.reverse ? -pointerDelta : pointerDelta;
    momentumSamples.push(pointerDelta);
    if (momentumSamples.length > 6) momentumSamples.shift();
    lastPointerX = event.clientX;
  });

  const endDrag = () => {
    if (activePointerId !== null && wrapper.hasPointerCapture?.(activePointerId)) {
      wrapper.releasePointerCapture(activePointerId);
    }

    activePointerId = null;
    shouldIgnorePointer = false;

    if (!isDragging) return;

    isDragging = false;
    wrapper.classList.remove(RUNTIME_CLASSES.dragging);

    const baseSpeed = getBaseSpeed();
    const releaseBoost = isMobileViewport() ? 0.2 : 1.5;
    const averageMomentum =
      momentumSamples.reduce((sum, sample) => sum + sample, 0) / (momentumSamples.length || 1);

    targetVelocity = Math.min(
      getMaxSpeed(),
      Math.max(baseSpeed, baseSpeed + Math.abs(averageMomentum) * releaseBoost)
    );
    velocity = Math.max(velocity, baseSpeed);
  };

  wrapper.addEventListener("pointerup", endDrag);
  wrapper.addEventListener("pointercancel", endDrag);

  return { mover, track, wrapper };
}

export function initTickers(configs = TICKER_CONFIGS) {
  configs.forEach(initTicker);
}

if (document.readyState === "complete") {
  initTickers();
} else {
  window.addEventListener("load", () => initTickers(), { once: true });
}
